import type { LocalPost, ServiceItem } from "../../services/gbp";
import type { GabaritoState } from "./components/gabarito-utils";
import { appendKeywords, splitKeywords } from "./components/gabarito-utils";
import { sanitizeKeywordSuggestions } from "./assistant-suggestions";
import type { Profile } from "./types";

export type PostDraftSuggestion = LocalPost & {
  id: string;
  title: string;
  imageKeyword: string;
  localImageName?: string;
  localImagePreview?: string;
};

const profileCityState = (profile: Pick<Profile, "city" | "state">) => {
  const cityParts = (profile.city || "").split(",").map((part) => part.trim());
  const city = cityParts[0] || "";
  const state = profile.state || cityParts[1] || "";

  return {
    city,
    state: state.toUpperCase(),
  };
};

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const unique = (values: string[]) => {
  const result: string[] = [];

  values.forEach((value) => {
    const cleanValue = value.trim();
    if (
      cleanValue &&
      !result.some((item) => normalizeText(item) === normalizeText(cleanValue))
    ) {
      result.push(cleanValue);
    }
  });

  return result;
};

const hasTerm = (context: string, terms: string[]) =>
  terms.some((term) => new RegExp(`\\b${term}\\b`, "i").test(context));

const getBusinessName = (profile: Profile) =>
  profile.name.split(" - ")[0]?.trim() || profile.name;

const inferSegments = (profile: Profile) => {
  const context = normalizeText(
    [profile.name, profile.category, ...(profile.secondaryCategories || [])].join(
      " ",
    ),
  );

  if (
    context.includes("balanca") ||
    context.includes("balancas") ||
    context.includes("afericao") ||
    context.includes("imetro") ||
    context.includes("inmetro")
  ) {
    return {
      main: "manutenção, aferição e automação de balanças",
      terms: [
        "manutenção de balanças",
        "aferição de balanças",
        "balança comercial",
        "balança industrial",
        "balança antropométrica",
        "empresa credenciada pelo Inmetro",
      ],
      services: [
        "manutenção de balanças",
        "aferição de balanças",
        "calibração de balanças comerciais",
        "manutenção de balança industrial",
        "aferição de balança antropométrica",
        "orçamento para manutenção de balança",
        "assistência técnica para balanças",
        "automação comercial para balanças",
        "empresa credenciada para aferição de balanças",
        "conserto de balanças comerciais",
      ],
    };
  }

  if (
    hasTerm(context, [
      "automotivo",
      "automotiva",
      "automotivos",
      "carro",
      "carros",
    ]) ||
    context.includes("som automotivo")
  ) {
    return {
      main: "som automotivo e acessórios para carros",
      terms: [
        "som automotivo",
        "acessórios automotivos",
        "instalação de som automotivo",
        "alarme automotivo",
        "multimídia automotiva",
        "alto falantes para carro",
      ],
      services: [
        "instalação de som automotivo",
        "montagem de som automotivo",
        "instalação de alarme automotivo",
        "instalação de central multimídia",
        "troca de alto falantes automotivos",
        "instalação de câmera de ré",
        "instalação de sensor de estacionamento",
        "instalação de película automotiva",
        "orçamento de acessórios automotivos",
        "manutenção de som automotivo",
      ],
    };
  }

  if (
    context.includes("pesca") ||
    context.includes("nautica") ||
    context.includes("barco") ||
    context.includes("caca")
  ) {
    return {
      main: "produtos de pesca, caça e artigos náuticos",
      terms: [
        "artigos de pesca",
        "equipamentos de pesca",
        "iscas e acessórios",
        "artigos náuticos",
        "motores de barco",
        "produtos para caça",
      ],
      services: [
        "venda de artigos de pesca",
        "venda de equipamentos de pesca",
        "orçamento de motores de barco",
        "venda de acessórios náuticos",
        "venda de iscas e anzóis",
        "venda de produtos para caça",
        "venda de acessórios para barco",
        "consultoria para pesca esportiva",
        "venda de materiais para pesca",
        "orçamento de produtos náuticos",
      ],
    };
  }

  const base = profile.category || "serviços e produtos";
  return {
    main: base,
    terms: [base],
    services: [
      `orçamento de ${base}`,
      `contratar ${base}`,
      `comprar ${base}`,
      `${base} perto de mim`,
      `${base} com atendimento local`,
      `${base} para empresas`,
      `${base} para residências`,
      `${base} com preço acessível`,
      `${base} com entrega rápida`,
      `${base} com especialista`,
    ],
  };
};

export const buildPublicBusinessDescription = ({
  profile,
  keywords,
}: {
  profile: Profile;
  keywords: string[];
}) => {
  const { city, state } = profileCityState(profile);
  const location = [city, state].filter(Boolean).join(", ");
  const segment = inferSegments(profile);
  const businessName = getBusinessName(profile);
  const cleanKeywords = sanitizeKeywordSuggestions(keywords, profile).slice(0, 4);
  const keywordText =
    cleanKeywords.length > 0
      ? `Quem procura por ${cleanKeywords.join(", ")} encontra na empresa opções alinhadas para compra, orçamento e atendimento local.`
      : "";
  const categoryText = profile.category ? ` como ${profile.category}` : "";
  const locationText = location ? ` em ${location}` : "";

  return `${businessName} atua${locationText}${categoryText}, com foco em ${segment.main}. A empresa ajuda clientes que buscam ${segment.terms
    .slice(0, 3)
    .join(", ")} com atendimento direto, orientação sobre produtos e soluções adequadas para cada necessidade. ${keywordText}`.trim();
};

export const buildCompleteGabarito = ({
  profile,
  existing,
  keywords,
}: {
  profile: Profile;
  existing: GabaritoState;
  keywords: string[];
}) => {
  const { city } = profileCityState(profile);
  const cleanKeywords = sanitizeKeywordSuggestions(keywords, profile);
  const segment = inferSegments(profile);
  const commentKeywords = [
    "obrigado",
    "atendimento",
    "orçamento",
    "disponibilidade",
    "qualidade",
    "equipe",
  ];

  return {
    ...existing,
    postKeywords: appendKeywords(existing.postKeywords, cleanKeywords),
    commentKeywords: appendKeywords(existing.commentKeywords, commentKeywords),
    postNotes:
      existing.postNotes ||
      `Usar chamadas com intenção de compra em ${city || "região local"}, citando ${segment.terms
        .slice(0, 4)
        .join(", ")} e convidando para orçamento, compra ou contato.`,
    commentNotes:
      existing.commentNotes ||
      "Responder com tom profissional, agradecer o comentário, citar atendimento e orientar o cliente a entrar em contato para orçamento ou disponibilidade.",
  };
};

export const buildServiceKeywordSuggestions = (
  profile: Profile,
  extraKeywords: string[] = [],
): ServiceItem[] => {
  const { city } = profileCityState(profile);
  const segment = inferSegments(profile);

  return unique([
    ...extraKeywords.map((k) => k.trim()).filter(Boolean),
    ...segment.services.map((service) => (city ? `${city} ${service}` : service)),
  ])
    .slice(0, 10)
    .map((label) => ({
      offered: true,
      label,
      categoryId: "custom_service",
      description: `Serviço sugerido para captar buscas com intenção de compra por "${label}". Revise antes de salvar no perfil.`,
    }));
};

export const buildPostDraftSuggestions = ({
  profile,
  keywords,
  imageNames,
}: {
  profile: Profile;
  keywords: string[];
  imageNames: string[];
}): PostDraftSuggestion[] => {
  const segment = inferSegments(profile);
  const { city } = profileCityState(profile);
  const cleanKeywords = sanitizeKeywordSuggestions(keywords, profile);
  const selectedKeywords =
    cleanKeywords.length > 0
      ? cleanKeywords
      : sanitizeKeywordSuggestions(segment.terms, profile);
  const images = imageNames.length > 0 ? imageNames : selectedKeywords;
  const locationText = city ? ` em ${city}` : "";

  const summaries = [
    `${profile.name} oferece ${segment.terms[0]}${locationText} para quem quer comprar com orientação e atendimento local. Fale com a equipe e confira as opções disponíveis.`,
    `Procurando ${selectedKeywords[0] || segment.terms[0]}? A ${profile.name} ajuda você a escolher a melhor opção, solicitar orçamento e tirar dúvidas antes da compra.`,
    `Na ${profile.name}, clientes encontram ${segment.terms
      .slice(0, 2)
      .join(" e ")} com atendimento pensado para quem busca qualidade, disponibilidade e compra segura${locationText}.`,
    `Quer comparar opções de ${segment.main}${locationText}? Visite ou fale com a ${profile.name} para receber orientação e conferir produtos e serviços disponíveis.`,
    `${selectedKeywords[1] || segment.terms[0]} é uma das buscas de clientes que procuram solução local. A ${profile.name} atende com informações claras e suporte para orçamento.`,
  ];

  return summaries.map((summary, index) => ({
    id: `post-draft-${index + 1}`,
    title: `Sugestão ${index + 1}`,
    summary,
    topicType: index === 1 ? "OFFER" : "STANDARD",
    languageCode: "pt",
    imageKeyword: images[index % images.length],
  }));
};

export const gabaritoImageNames = (images: GabaritoState["images"]) =>
  images.map((image) => image.keyword || image.title || image.fileName);

export const gabaritoKeywords = (gabarito: Pick<GabaritoState, "postKeywords">) =>
  splitKeywords(gabarito.postKeywords);
